import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { VipProduct, VipBenefit } from "../api/types";

// Round 44 follow-up client item — "VIP Products and Benefits must be
// combined... When I click Add VIP Product or Edit, I should be able to
// configure everything for that specific VIP tier in the same form."
// VipBenefitsPage.tsx (a separate page, "Manage Benefits" link) is REMOVED
// by this change — every field it used to hold now lives in this one
// modal, keyed to the tier being added/edited. See App.tsx/Sidebar.tsx,
// which no longer route/link to it.
//
// The six per-tier BENEFIT_SLOTS below are the client's named benefit list
// minus "VIP medal" — vip_products already HAS its own medal_png_asset_url/
// medal_svga_asset_url (the one big centered medal Android actually shows
// on the tab screen), and the previous round's separate Benefits page
// already flagged that a benefit_type="medal" row is not rendered by
// Android anywhere — it would just be a second, unused medal field with no
// visual effect. Rather than ship that confusing duplicate into the merged
// form, "VIP Medal" is covered once, in the tier-level Medal PNG/SVGA
// fields below (same place it already was), and the benefit slots here are
// exactly the other six the client named: title, frame, user card, chat
// bubble, mic wave, vehicle.
const BENEFIT_SLOTS: { type: VipBenefit["benefit_type"]; label: string }[] = [
  { type: "title", label: "VIP Title" },
  { type: "frame", label: "VIP Frame" },
  { type: "user_card", label: "User Card" },
  { type: "chat_bubble", label: "Room Chat Bubble" },
  { type: "mic_wave", label: "Mic Wave" },
  { type: "vehicle", label: "Vehicle" },
];

type BenefitDraft = {
  id?: number;
  png_asset_url: string;
  svga_asset_url: string;
  is_enabled: boolean;
  duration_days: string;
};

const EMPTY_BENEFIT: BenefitDraft = { png_asset_url: "", svga_asset_url: "", is_enabled: true, duration_days: "" };

function emptyBenefits(): Record<string, BenefitDraft> {
  const map: Record<string, BenefitDraft> = {};
  for (const slot of BENEFIT_SLOTS) map[slot.type] = { ...EMPTY_BENEFIT };
  return map;
}

export function VipProductsPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<VipProduct>("/admin/vip-products");
  const [editing, setEditing] = useState<Partial<VipProduct> | null>(null);
  const [benefits, setBenefits] = useState<Record<string, BenefitDraft>>(emptyBenefits());
  const [isNew, setIsNew] = useState(false);
  const [isLoadingBenefits, setIsLoadingBenefits] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  function openAdd() {
    setEditing({ is_active: true, is_visible: true, tier: "" });
    setBenefits(emptyBenefits());
    setIsNew(true);
  }

  async function openEdit(row: VipProduct) {
    setEditing(row);
    setIsNew(false);
    setIsLoadingBenefits(true);
    try {
      // Pull this tier's existing benefit rows into the same modal — the
      // whole point of combining the two pages is that opening Edit shows
      // everything about this tier at once, not a drill-down.
      const existing = await apiRequest<VipBenefit[]>("/admin/vip-benefits", {
        params: { product_id: row.product_id },
      });
      const draft = emptyBenefits();
      for (const b of existing) {
        if (draft[b.benefit_type]) {
          draft[b.benefit_type] = {
            id: b.id,
            png_asset_url: b.png_asset_url ?? "",
            svga_asset_url: b.svga_asset_url ?? "",
            is_enabled: b.is_enabled,
            duration_days: b.duration_days != null ? String(b.duration_days) : "",
          };
        }
      }
      setBenefits(draft);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not load this tier's benefits", "error");
      setBenefits(emptyBenefits());
    } finally {
      setIsLoadingBenefits(false);
    }
  }

  function updateBenefit(type: string, patch: Partial<BenefitDraft>) {
    setBenefits((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } }));
  }

  async function save() {
    if (!editing?.product_id || !editing.tier || !editing.duration_days || editing.coins_price == null) {
      showToast("Tier key, tier label, duration, and coin price are required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const productBody = {
        product_id: editing.product_id,
        tier: editing.tier,
        duration_days: Number(editing.duration_days),
        coins_price: Number(editing.coins_price),
        is_active: editing.is_active ?? true,
        is_visible: editing.is_visible ?? true,
        sort_order: Number(editing.sort_order) || 0,
        background_image_url: editing.background_image_url || null,
        medal_png_asset_url: editing.medal_png_asset_url || null,
        medal_svga_asset_url: editing.medal_svga_asset_url || null,
        // Play Console Product ID / Play Store price are gone from this
        // workflow entirely (client item 1) — price_display is explicitly
        // written as null on every save from this form, not just left off
        // the form, so a tier edited here can never carry a stale legacy
        // price forward from before this change.
        price_display: null,
      };
      if (isNew) {
        await apiRequest("/admin/vip-products", { method: "POST", body: productBody });
      } else {
        await apiRequest(`/admin/vip-products/${editing.product_id}`, { method: "PATCH", body: productBody });
      }

      // One compound save covers the tier AND its six benefit slots, per
      // the client's explicit ask ("I should be able to configure
      // everything for that specific VIP tier in the same form"). A slot
      // with both asset URLs blank and no existing row is simply skipped
      // (never creates an empty benefit); a slot that existed but was
      // cleared back to blank is deleted — "no asset configured" and "no
      // benefit row at all" are the same state everywhere else in this
      // schema (see migration 0102's own comments), so clearing a slot in
      // this form removes it rather than leaving a hollow row behind.
      for (const slot of BENEFIT_SLOTS) {
        const draft = benefits[slot.type];
        const hasAsset = !!(draft.png_asset_url || draft.svga_asset_url);
        if (!hasAsset) {
          if (draft.id) {
            await apiRequest(`/admin/vip-benefits/${draft.id}`, { method: "DELETE" });
          }
          continue;
        }
        const benefitBody = {
          product_id: editing.product_id,
          benefit_type: slot.type,
          png_asset_url: draft.png_asset_url || null,
          svga_asset_url: draft.svga_asset_url || null,
          is_enabled: draft.is_enabled,
          duration_days: draft.duration_days ? Number(draft.duration_days) : null,
          sort_order: 0,
        };
        if (draft.id) {
          await apiRequest(`/admin/vip-benefits/${draft.id}`, { method: "PATCH", body: benefitBody });
        } else {
          await apiRequest("/admin/vip-benefits", { method: "POST", body: benefitBody });
        }
      }

      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  const columns: Column<VipProduct>[] = [
    {
      header: "Tab",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.medal_png_asset_url || row.medal_svga_asset_url ? (
            <img src={row.medal_png_asset_url ?? undefined} className="h-8 w-8 rounded-full object-cover" alt="" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-surface-muted" />
          )}
          <span className="font-mono text-xs">{row.product_id}</span>
        </div>
      ),
    },
    { header: "Tier", render: (row) => <Badge tone="gold">{row.tier.toUpperCase()}</Badge> },
    { header: "Order", render: (row) => row.sort_order },
    { header: "Duration", render: (row) => `${row.duration_days} days` },
    { header: "Coin Price", render: (row) => `${row.coins_price} coins` },
    {
      header: "Tab Visible",
      render: (row) => (row.is_visible ? <Badge tone="success">Visible</Badge> : <Badge tone="neutral">Hidden</Badge>),
    },
    {
      header: "Purchasable",
      render: (row) => (row.is_active ? <Badge tone="success">On</Badge> : <Badge tone="warn">Off</Badge>),
    },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => openEdit(row)}>
            Edit
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="VIP Products">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        One form per VIP tier covers everything: tab visibility, duration, coin price, background, medal, and all
        six benefits. VIP is purchased with in-app coins only — there is no Google Play product or Play Store price
        anywhere in this workflow.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={openAdd}>+ Add VIP Product</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.product_id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No VIP products configured"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={isNew ? "Add VIP Product" : "Edit VIP Product"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} isLoading={isSubmitting || isLoadingBenefits}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase text-ink-faint">Tier</h3>
            <Input
              label="Tier key (internal id, e.g. vip1 — must be unique per tier)"
              value={editing?.product_id ?? ""}
              disabled={!isNew}
              onChange={(e) => setEditing((p) => ({ ...p, product_id: e.target.value }))}
            />
            <Input
              label="Tier label"
              value={editing?.tier ?? ""}
              onChange={(e) => setEditing((p) => ({ ...p, tier: e.target.value }))}
            />
            <Input
              label="Duration (days)"
              type="number"
              value={editing?.duration_days ?? ""}
              onChange={(e) => setEditing((p) => ({ ...p, duration_days: Number(e.target.value) }))}
            />
            <Input
              label="Coin purchase price"
              type="number"
              value={editing?.coins_price ?? ""}
              onChange={(e) => setEditing((p) => ({ ...p, coins_price: Number(e.target.value) }))}
            />
            <Input
              label="Tab order (VIP1 = 0, VIP2 = 1, ...)"
              type="number"
              value={editing?.sort_order ?? 0}
              onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))}
            />
            <Select
              label="Tab visibility"
              value={editing?.is_visible === false ? "hidden" : "visible"}
              onChange={(e) => setEditing((p) => ({ ...p, is_visible: e.target.value === "visible" }))}
            >
              <option value="visible">Visible — shown as a tab in Android</option>
              <option value="hidden">Hidden — tab removed entirely, regardless of Purchasable</option>
            </Select>
            <Select
              label="Purchasable"
              value={editing?.is_active === false ? "off" : "on"}
              onChange={(e) => setEditing((p) => ({ ...p, is_active: e.target.value === "on" }))}
            >
              <option value="on">On — users can buy this tier</option>
              <option value="off">Off — tab can still show (if visible), Buy disabled</option>
            </Select>
          </div>

          <div className="space-y-3 border-t border-divider pt-4">
            <h3 className="text-xs font-semibold uppercase text-ink-faint">Background &amp; Medal</h3>
            <Input
              label="Background image URL (full-bleed, behind the medal)"
              value={editing?.background_image_url ?? ""}
              onChange={(e) => setEditing((p) => ({ ...p, background_image_url: e.target.value }))}
            />
            <Input
              label="Medal PNG asset URL"
              value={editing?.medal_png_asset_url ?? ""}
              onChange={(e) => setEditing((p) => ({ ...p, medal_png_asset_url: e.target.value }))}
            />
            <Input
              label="Medal SVGA asset URL (optional — takes priority over PNG when set)"
              value={editing?.medal_svga_asset_url ?? ""}
              onChange={(e) => setEditing((p) => ({ ...p, medal_svga_asset_url: e.target.value }))}
            />
          </div>

          <div className="space-y-4 border-t border-divider pt-4">
            <h3 className="text-xs font-semibold uppercase text-ink-faint">Benefits</h3>
            {isLoadingBenefits ? (
              <p className="text-xs text-ink-faint">Loading this tier's existing benefits…</p>
            ) : (
              BENEFIT_SLOTS.map((slot) => {
                const draft = benefits[slot.type];
                return (
                  <div key={slot.type} className="rounded-lg border border-divider p-3 space-y-2">
                    <p className="text-sm font-medium">{slot.label}</p>
                    <Input
                      label="PNG asset URL"
                      value={draft.png_asset_url}
                      onChange={(e) => updateBenefit(slot.type, { png_asset_url: e.target.value })}
                    />
                    <Input
                      label="SVGA asset URL (optional — takes priority over PNG when set)"
                      value={draft.svga_asset_url}
                      onChange={(e) => updateBenefit(slot.type, { svga_asset_url: e.target.value })}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Select
                        label="Availability"
                        value={draft.is_enabled ? "on" : "off"}
                        onChange={(e) => updateBenefit(slot.type, { is_enabled: e.target.value === "on" })}
                      >
                        <option value="on">Enabled — active perk</option>
                        <option value="off">Disabled — shown locked</option>
                      </Select>
                      <Input
                        label="Duration (days, optional)"
                        type="number"
                        value={draft.duration_days}
                        onChange={(e) => updateBenefit(slot.type, { duration_days: e.target.value })}
                      />
                    </div>
                    <p className="text-xs text-ink-faint">
                      Leave both asset URLs blank to remove this benefit from the tier entirely.
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
